import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { updatePassword, resetUpdate } from "../slices/profileSlice";
import { usePermissions } from "../utils/permissions";
import BottomNavbar from "../componets/BottomNavbar";

const ChangePassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.user);
  const { loading, isUpdated } = useSelector((state) => state.profile);

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const { hasPermission } = usePermissions();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    } else if (!hasPermission("profile", "update")) {
      navigate("/my-business");
    }
  }, [isAuthenticated, navigate, hasPermission]);

  useEffect(() => {
    if (isUpdated) {
      dispatch(resetUpdate());
      // Optionally logout user after password change for security
      // dispatch(logoutUser());
      // navigate("/login");
    }
  }, [dispatch, isUpdated, navigate]);

  const validateForm = () => {
    const errors = {};

    if (!formData.oldPassword) {
      errors.oldPassword = "Current password is required";
    }

    if (!formData.newPassword) {
      errors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your new password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (formData.oldPassword === formData.newPassword) {
      errors.newPassword = "New password must be different from current password";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(
        updatePassword({
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        })
      ).unwrap();
      // Clear form after successful update
      setFormData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      // Error is handled by the slice with toast
    }
  };

  return (
    <div style={styles.container}>
      <div style={{
        ...styles.content,
        padding: isMobile ? "16px" : "24px",
        paddingBottom: isMobile ? "100px" : "100px",
      }}>
        <div style={{
          ...styles.card,
          padding: isMobile ? "20px 16px" : "30px",
        }}>
          <h2 style={{
            ...styles.title,
            fontSize: isMobile ? "24px" : "28px",
          }}>Change Password</h2>
          <p style={styles.subtitle}>
            Update your password to keep your account secure.
          </p>

          <form onSubmit={handleSubmit} style={{
            ...styles.form,
            gap: isMobile ? "16px" : "20px",
          }}>
            <div style={styles.formGroup}>
              <label htmlFor="oldPassword" style={styles.label}>
                Current Password <span style={styles.required}>*</span>
              </label>
              <div style={styles.passwordContainer}>
                <input
                  type={showOldPassword ? "text" : "password"}
                  id="oldPassword"
                  name="oldPassword"
                  value={formData.oldPassword}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...styles.passwordInput,
                    borderColor: validationErrors.oldPassword ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter your current password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  style={styles.passwordToggle}
                  disabled={loading}
                >
                  {showOldPassword ? "Hide" : "Show"}
                </button>
              </div>
              {validationErrors.oldPassword && (
                <span style={styles.errorText}>
                  {validationErrors.oldPassword}
                </span>
              )}
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="newPassword" style={styles.label}>
                New Password <span style={styles.required}>*</span>
              </label>
              <div style={styles.passwordContainer}>
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...styles.passwordInput,
                    borderColor: validationErrors.newPassword ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter your new password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={styles.passwordToggle}
                  disabled={loading}
                >
                  {showNewPassword ? "Hide" : "Show"}
                </button>
              </div>
              {validationErrors.newPassword && (
                <span style={styles.errorText}>
                  {validationErrors.newPassword}
                </span>
              )}
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="confirmPassword" style={styles.label}>
                Confirm New Password <span style={styles.required}>*</span>
              </label>
              <div style={styles.passwordContainer}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...styles.passwordInput,
                    borderColor: validationErrors.confirmPassword ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Confirm your new password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={styles.passwordToggle}
                  disabled={loading}
                >
                  {showConfirmPassword ? "Hide" : "Show"}
                </button>
              </div>
              {validationErrors.confirmPassword && (
                <span style={styles.errorText}>
                  {validationErrors.confirmPassword}
                </span>
              )}
            </div>

            <button
              type="submit"
              style={{
                ...styles.button,
                padding: isMobile ? "12px 20px" : "12px 24px",
              }}
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>

          <div style={{
            ...styles.actions,
            marginTop: isMobile ? "16px" : "20px",
          }}>
            <Link to="/profile" style={styles.linkButton}>
              Back to Profile
            </Link>
          </div>
        </div>
      </div>
      <BottomNavbar />
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    display: "flex",
    flexDirection: "column",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, 'Lucida Grande', Arial, Ubuntu, Cantarell, 'Fira Sans', sans-serif",
    paddingBottom: "80px",
  },
  content: {
    maxWidth: "600px",
    margin: "0 auto",
    padding: "24px",
    minHeight: "calc(100vh - 80px)",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "30px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e9edef",
  },
  title: {
    fontSize: "28px",
    fontWeight: "500",
    color: "#111b21",
    marginBottom: "8px",
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: "14px",
    color: "#667781",
    marginBottom: "24px",
    fontWeight: "400",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
  },
  form: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
    letterSpacing: "-0.01em",
  },
  required: {
    color: "#dc3545",
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    fontSize: "15px",
    fontWeight: "400",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    color: "#111b21",
    letterSpacing: "-0.01em",
    boxSizing: "border-box",
  },
  passwordContainer: {
    position: "relative",
    width: "100%",
  },
  passwordInput: {
    paddingRight: "70px",
  },
  passwordToggle: {
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    color: "#00a884",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "400",
    padding: "4px 8px",
    borderRadius: "4px",
    transition: "color 0.2s",
    letterSpacing: "-0.01em",
  },
  inputError: {
    borderColor: "#dc3545",
  },
  errorText: {
    color: "#dc3545",
    fontSize: "12px",
    marginTop: "4px",
    display: "block",
    fontWeight: "400",
  },
  button: {
    width: "100%",
    padding: "12px 24px",
    fontSize: "15px",
    fontWeight: "400",
    color: "#ffffff",
    backgroundColor: "#dc3545",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    marginTop: "10px",
    transition: "background-color 0.2s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    letterSpacing: "-0.01em",
  },
  actions: {
    marginTop: "20px",
    textAlign: "center",
    paddingTop: "20px",
    borderTop: "1px solid #e9edef",
  },
  linkButton: {
    color: "#00a884",
    textDecoration: "none",
    fontSize: "14px",
    fontWeight: "400",
    letterSpacing: "-0.01em",
  },
};

export default ChangePassword;

